/** @jsx jsx */
import { css, jsx } from '@emotion/react';

import * as React from 'react';
import { Fragment, useContext, useEffect, useState } from 'react';

import { Button } from '@coursera/cds-core';
import { FlagIcon } from '@coursera/cds-icons';
import { useTracker } from '@coursera/event-pulse/react';

import { PlagiarismContext } from 'bundles/author-assignment-grading/components/common/components/GraderModuleSidebar/PlagiarismContext';
import PlagiarismDrawer from 'bundles/grading/components/staff-graded-assignments/plagiarism/PlagiarismDrawer';

import _t from 'i18n!nls/grading';

const styles = {
  integrityFlag: css`
    margin-top: var(--cds-spacing-150);
    position: relative;
  `,
  badge: css`
    position: absolute;
    top: -8px;
    right: -8px;
    background-color: var(--cds-color-feedback-error);
    color: var(--cds-color-neutral-primary-invert);
    display: inline-flex;
    justify-content: center;
    align-items: center;
    border-radius: 1000px;
    font-size: 10px;
    font-weight: bold;
    line-height: 1;
    min-width: 18px;
    height: 18px;
  `,
};

type IntegrityFlagProps = {
  submitterId: string;
  submitterName?: string;
  courseId: string;
  assignmentId?: string;
};

const IntegrityFlag = ({ submitterId, submitterName, courseId, assignmentId }: IntegrityFlagProps) => {
  const track = useTracker();

  const unversionedAssignmentId = assignmentId?.split('@')[0];
  const { cheatingCaseStaffComments, activeIntegrityFlagCount, setActiveIntegrityFlagCount, vivaExamEnabled } =
    useContext(PlagiarismContext);

  useEffect(() => {
    const hasFlag = Boolean(cheatingCaseStaffComments && cheatingCaseStaffComments.length);

    if (hasFlag) {
      // if learner has a cheating case, increment the active integrity flag count
      setActiveIntegrityFlagCount((prevCount) => {
        return prevCount < 2 ? prevCount + 1 : prevCount;
      });
    }
  }, [cheatingCaseStaffComments, setActiveIntegrityFlagCount]);

  const [isPlagiarismDrawerOpen, setPlagiarismDrawerOpen] = useState(false);

  const togglePlagiarismDrawer = () => {
    track('click_button', {
      button: {
        name: 'integrity_check_view_integrity_flags',
      },
      product: {
        type: 'course',
        id: courseId,
      },
      assignment: {
        id: unversionedAssignmentId ?? '',
      },
    });
    setPlagiarismDrawerOpen((prevState) => !prevState);
  };

  if (!vivaExamEnabled) {
    return null;
  }

  return (
    <Fragment>
      <Button
        iconPosition="before"
        size="small"
        variant="ghost"
        icon={<FlagIcon size="small" aria-hidden="true" />}
        onClick={togglePlagiarismDrawer}
        css={styles.integrityFlag}
      >
        {_t('View integrity flags')}
        {(activeIntegrityFlagCount || 0) > 0 && <div css={styles.badge}>{activeIntegrityFlagCount}</div>}
      </Button>
      <PlagiarismDrawer
        key={`plagiarism-${submitterId}`}
        isOpen={isPlagiarismDrawerOpen}
        onClose={() => setPlagiarismDrawerOpen(false)}
        submitterId={submitterId}
        submitterName={submitterName}
      />
    </Fragment>
  );
};

export default IntegrityFlag;
